if (!customElements.get("color-swatch")) {
  class ColorSwatch extends HTMLElement {
    constructor() {
      super();
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.optionNodes = this.querySelectorAll("[data-value]");
      this.init();
    }

    disconnectedCallback() {
      this.optionNodes?.forEach((button) =>
        button.removeEventListener("mouseenter", this.onMouseEnter)
      );
      this.removeEventListener("mouseleave", this.onMouseLeave);
      this.removeEventListener("focusin", this.onFocusIn);
      this.removeEventListener("focusout", this.onFocusOut);
      this.removeEventListener("click", this.onClick);
    }

    init() {
      this.productCard = this.closest(".product-card");
      this.mainImage = this.productCard?.querySelector(
        ".product-card__image--main img"
      );
      this.previewActive = false;
      this.touchSelected = null;

      this.getVariantData();
      this.initOptionSwatches();

      this.optionNodes.forEach((button) =>
        button.addEventListener("mouseenter", this.onMouseEnter)
      );
      this.addEventListener("mouseleave", this.onMouseLeave);
      this.addEventListener("focusin", this.onFocusIn);
      this.addEventListener("focusout", this.onFocusOut);
      this.addEventListener("click", this.onClick);

      this.selected = this.querySelector('a[aria-selected="true"]');
      this.initialSelected = this.selected;
    }

    normalize(value) {
      return (value || "").toString().trim().toLowerCase();
    }

    matchingVariants(optionPosition, optionValue) {
      const key = `option${optionPosition}`;
      const wanted = this.normalize(optionValue);
      return this.variantData.filter((variant) =>
        this.normalize(variant?.[key]) === wanted
      );
    }

    initOptionSwatches() {
      this.optionNodes.forEach((optNode) => {
        const { optionPosition, value: optionValue } = optNode.dataset;
        const matching = this.matchingVariants(optionPosition, optionValue);
        if (!matching.length) return;

        const previewVariant =
          matching.find((variant) => variant.available && variant.featured_image?.src) ||
          matching.find((variant) => variant.featured_image?.src) ||
          matching.find((variant) => variant.available) ||
          matching[0];
        const linkVariant =
          matching.find((variant) => variant.available) || previewVariant || matching[0];

        if (linkVariant?.id) {
          try {
            const url = new URL(optNode.href, window.location.origin);
            url.searchParams.set("variant", linkVariant.id);
            optNode.href = url.toString();
          } catch (_) {}
        }

        const variantImage = previewVariant?.featured_image?.src || "";
        if (variantImage) {
          optNode.dataset.src =
            typeof getSizedImageUrl === "function"
              ? getSizedImageUrl(variantImage, "900x")
              : variantImage;
          if (typeof getSrcset === "function") {
            optNode.dataset.srcset = getSrcset(variantImage);
          }
        }
      });
    }

    captureBaseState() {
      if (!this.mainImage || this.previewActive) return;
      this.baseSrc = this.mainImage.getAttribute("src") || this.mainImage.src || "";
      this.baseSrcset = this.mainImage.getAttribute("srcset") || "";
      this.baseSelected = this.querySelector('a[aria-selected="true"]');
    }

    selectNode(target) {
      this.querySelectorAll('[aria-selected="true"]').forEach((node) => {
        if (node !== target) node.removeAttribute("aria-selected");
      });
      target.setAttribute("aria-selected", "true");
      this.selected = target;
    }

    applyPreview(target) {
      if (!target || !this.mainImage) return false;
      const { src, srcset } = target.dataset;
      if (!src) return false;

      this.captureBaseState();
      this.mainImage.src = src;
      if (srcset) this.mainImage.srcset = srcset;
      else this.mainImage.removeAttribute("srcset");

      this.productCard?.classList.add("peakstyle-swatch-preview-active");
      this.selectNode(target);
      this.previewActive = true;
      return true;
    }

    restorePreview() {
      if (!this.previewActive || !this.mainImage) return;
      if (this.baseSrc) this.mainImage.src = this.baseSrc;
      if (this.baseSrcset) this.mainImage.srcset = this.baseSrcset;
      else this.mainImage.removeAttribute("srcset");

      this.productCard?.classList.remove("peakstyle-swatch-preview-active");
      this.querySelectorAll('[aria-selected="true"]').forEach((node) =>
        node.removeAttribute("aria-selected")
      );
      if (this.baseSelected?.isConnected) {
        this.baseSelected.setAttribute("aria-selected", "true");
        this.selected = this.baseSelected;
      }
      this.previewActive = false;
      this.touchSelected = null;
    }

    isTouchLike() {
      return window.matchMedia("(hover: none), (pointer: coarse)").matches;
    }

    onMouseEnter(event) {
      if (this.isTouchLike()) return;
      this.applyPreview(event.currentTarget);
    }

    onMouseLeave() {
      if (this.isTouchLike()) return;
      this.restorePreview();
    }

    onFocusIn(event) {
      const target = event.target.closest?.("[data-value]");
      if (target && this.contains(target)) this.applyPreview(target);
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      if (!this.isTouchLike()) this.restorePreview();
    }

    onClick(event) {
      if (!this.isTouchLike()) return;
      const target = event.target.closest?.("[data-value]");
      if (!target || !this.contains(target) || !target.dataset.src) return;

      if (this.previewActive && this.touchSelected === target) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (this.applyPreview(target)) this.touchSelected = target;
    }

    getVariantData() {
      if (Array.isArray(this.variantData) && this.variantData.length) return;
      try {
        const parsed = JSON.parse(this.nextElementSibling?.textContent || "[]");
        this.variantData = Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        this.variantData = [];
      }
    }
  }
  customElements.define("color-swatch", ColorSwatch);
}

(() => {
  if (window.PeakStyleCrossSellGuard) return;
  window.PeakStyleCrossSellGuard = true;

  const norm = (value = "") => value.toString().trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const audienceFromText = (value = "") => {
    const text = norm(value);
    if (/\b(damen|frauen|women|woman)\b/.test(text) || /\bscott\s+w\b/.test(text)) return "damen";
    if (/\b(herren|manner|maenner|men|man)\b/.test(text) || /\bscott\s+m\b/.test(text)) return "herren";
    if (/\b(madchen|maedchen|girls?)\b/.test(text)) return "madchen";
    if (/\b(jungen|boys?)\b/.test(text)) return "jungen";
    if (/\b(kinder|kids?|youth)\b/.test(text)) return "kinder";
    return "unisex";
  };

  const compatibleAudience = (source, candidate) => {
    if (!candidate) return false;
    switch (source) {
      case "herren": return candidate === "herren" || candidate === "unisex";
      case "damen": return candidate === "damen" || candidate === "unisex";
      case "jungen": return candidate === "jungen" || candidate === "kinder";
      case "madchen": return candidate === "madchen" || candidate === "kinder";
      case "kinder": return ["jungen", "madchen", "kinder"].includes(candidate);
      default: return ["herren", "damen", "unisex"].includes(candidate);
    }
  };

  const colorFamilies = (value = "") => {
    const text = norm(value);
    const families = new Set();
    const tests = [
      ["black", /black|schwarz|night|onyx|graphite/],
      ["white", /white|weiss|weis|snow|ivory|cotton/],
      ["grey", /grey|gray|grau|anthracite|slate|stone|silver|spray/],
      ["blue", /blue|blau|navy|aqua|turquoise|turkis|ocean|marine|denim|azure/],
      ["green", /green|grun|gruen|olive|moss|forest|lime|khaki/],
      ["red", /red|rot|burgundy|crimson|wine/],
      ["orange", /orange|coral|apricot|gleam|mandarin|rust/],
      ["yellow", /yellow|gelb|gold|mustard/],
      ["pink", /pink|rosa|rose|magenta|fuchsia/],
      ["purple", /purple|violet|lila|lavender|plum/],
      ["brown", /brown|braun|beige|sand|tan|taupe|camel|toast/]
    ];
    tests.forEach(([family, re]) => { if (re.test(text)) families.add(family); });
    return families;
  };

  const selectedSourceColor = () => {
    const fields = Array.from(document.querySelectorAll('product-info variant-picker [data-picker-field]'));
    const colorField = fields.find((field) => {
      const name = norm(field.dataset.optionName || "");
      return name.includes("farbe") || name.includes("color") || name.includes("colour");
    });
    if (!colorField) return "";
    const select = colorField.querySelector("select");
    if (select) return select.value || "";
    return colorField.querySelector('input[type="radio"]:checked')?.value || "";
  };

  const sourceAudience = () => {
    const h1 = document.querySelector('product-info h1, .f-product-single__title, h1');
    return audienceFromText(`${h1?.textContent || ""} ${location.pathname}`);
  };

  const cardAudience = (card) => {
    const marker = card.querySelector('[data-peakstyle-card-audience]');
    const explicit = norm(marker?.dataset.peakstyleCardAudience || "");
    if (explicit) return explicit;
    return audienceFromText(card.querySelector('.product-card__title')?.textContent || "");
  };

  const cardColorVariants = (card) => {
    const swatch = card.querySelector('color-swatch');
    if (!swatch) return [];
    let variants = [];
    try {
      variants = swatch.variantData || JSON.parse(swatch.nextElementSibling?.textContent || "[]");
    } catch (_) {
      return [];
    }
    const optionNode = swatch.querySelector('[data-option-position]');
    const position = Number(optionNode?.dataset.optionPosition || 0);
    if (!position) return [];
    return variants.filter((variant) => variant.available !== false && variant[`option${position}`]).map((variant) => ({
      variant,
      color: variant[`option${position}`],
      families: colorFamilies(variant[`option${position}`])
    }));
  };

  const bestColorMatch = (card, sourceColor) => {
    const sourceFamilies = colorFamilies(sourceColor);
    const variants = cardColorVariants(card);
    let best = null;
    let bestScore = -1;
    variants.forEach((item) => {
      let score = 0;
      if (norm(item.color) === norm(sourceColor)) score += 100;
      item.families.forEach((family) => {
        if (sourceFamilies.has(family)) score += 30;
        else if (["black", "white", "grey"].includes(family)) score += 3;
      });
      if (sourceFamilies.size === 0 && item.families.size > 0) score += 1;
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    });
    return { best, score: Math.max(bestScore, 0) };
  };

  const applyVariantToCard = (card, match) => {
    const item = match?.best;
    if (!item?.variant) return;
    const variant = item.variant;
    const swatch = card.querySelector('color-swatch');
    const option = swatch && Array.from(swatch.querySelectorAll('[data-value]')).find((node) => norm(node.dataset.value) === norm(item.color));
    if (option) {
      option.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
    } else if (variant.featured_image?.src) {
      const image = card.querySelector('.product-card__image--main img');
      if (image) {
        image.src = typeof getSizedImageUrl === "function" ? getSizedImageUrl(variant.featured_image.src, "900x") : variant.featured_image.src;
        if (typeof getSrcset === "function") image.srcset = getSrcset(variant.featured_image.src);
      }
    }
    card.querySelectorAll('a.product-card__link, a.full-width-link').forEach((link) => {
      const url = new URL(link.href, location.origin);
      url.searchParams.set('variant', variant.id);
      link.href = url.toString();
    });
  };

  const getColumns = (container) => {
    const seen = new Set();
    return Array.from(container.querySelectorAll('.f-column')).filter((column) => {
      const card = column.querySelector(':scope > .product-card, .product-card');
      if (!card || seen.has(card)) return false;
      seen.add(card);
      return true;
    });
  };

  const processBlock = (block) => {
    const container = block.querySelector('[data-products-container]');
    if (!container) {
      block.dataset.peakstyleCrossSellReady = "true";
      return;
    }

    const source = sourceAudience();
    const sourceColor = selectedSourceColor();
    const columns = getColumns(container);
    const ranked = [];

    columns.forEach((column, index) => {
      const card = column.querySelector('.product-card');
      if (!card) return;
      const audience = cardAudience(card);
      if (!compatibleAudience(source, audience)) {
        column.remove();
        return;
      }
      const match = bestColorMatch(card, sourceColor);
      applyVariantToCard(card, match);
      ranked.push({ column, score: match.score, index });
    });

    const parentGroups = new Map();
    ranked.forEach((item) => {
      const parent = item.column.parentElement;
      if (!parentGroups.has(parent)) parentGroups.set(parent, []);
      parentGroups.get(parent).push(item);
    });

    parentGroups.forEach((items, parent) => {
      const desiredOrder = [...items].sort((a, b) => b.score - a.score || a.index - b.index);
      desiredOrder.forEach(({ column }) => parent.appendChild(column));
    });

    block.dataset.peakstyleCrossSellReady = "true";
    window.dispatchEvent(new Event('resize'));
  };

  const processAll = (force = false) => {
    document.querySelectorAll('.template-product .product-recommendations').forEach((block) => {
      if (force || block.dataset.peakstyleCrossSellReady !== "true") processBlock(block);
    });
  };

  const style = document.createElement('style');
  style.textContent = '.template-product .product-recommendations:not([data-peakstyle-cross-sell-ready="true"]){visibility:hidden}';
  document.head.appendChild(style);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => processAll(false), { once: true });
  else processAll(false);

  document.addEventListener('variant:changed', () => window.setTimeout(() => processAll(true), 0));
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('product-info variant-picker')) window.setTimeout(() => processAll(true), 0);
  });

  new MutationObserver(() => processAll(false)).observe(document.documentElement, { childList: true, subtree: true });
})();
